import React, { useEffect, useState } from "react";
import { Button, Form } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useFilters } from "../../data/useFilters";
import { useSearchResults } from "../../data/useSearchResults";
import useMobileDetector from "../../utils/useMobileDetector";
import CustomIcon from "./CustomIcon";
import FilterHeader from "./FilterHeader";
import FilterRadio from "./Types/FilterRadio";
import FilterCheckbox from "./Types/FilterCheckbox";
import FilterRange from "./Types/FilterRange";
import {
  CustomPanel,
  ContentPanel,
  FooterActions,
  CustomHeader,
  CustomCollapseArea,
  FiltersWrapper,
} from "./styles";
import FiltersSkeleton from "../Skeleton/FiltersSkeleton";
import { debounceAction } from "../../utils/helpers";
import { useConfig } from "../../data/useConfig";

const Filters = ({
  closeSidebar,
  collapsed,
  layoutType,
  showLabel = false,
  enableButton,
}) => {
  const { filterDirection, areFiltersAvailable, setAreFiltersAvailable } =
    useConfig();
  const {
    isLoading,
    data,
    changeFilter,
    selectedFilters,
    changePage,
    updateFiltersURL,
  } = useFilters();
  const { isDesktop } = useMobileDetector();
  const { data: searchDataResults } = useSearchResults();

  const [activeKey, setActiveKey] = useState([]);

  const [form] = Form.useForm();

  useEffect(() => {
    document.addEventListener("click", ({ target }) => {
      if (
        !target.closest(".bnly-search__filter-name.ant-collapse-item-active")
      ) {
        const overlay = document.querySelector(".bnly-search__overlay");

        if (overlay) {
          overlay.style.display = "none";
          handleChangeCollapse(null);
        }
      }
    });
  }, []);

  useEffect(() => {
    updateFiltersURL();

    if (selectedFilters?.length > 0) {
      changePage(1);
    } else {
      form.resetFields();
    }
  }, [selectedFilters]);

  useEffect(() => {
    if (data) {
      const filtersAvailable = Object.values(
        data?.meta?.filters_available
      )?.filter((filter) => Object.keys(filter?.values)?.length > 1);

      if (filtersAvailable.length) {
        setAreFiltersAvailable(true);
      } else {
        setAreFiltersAvailable(false);
      }
    }

    const hasActiveFilter = document.querySelector(
      ".ant-collapse-content-active"
    );

    if (!hasActiveFilter) {
      const overlay = document.querySelector(".bnly-search__overlay");
      if (overlay) {
        overlay.style.display = "none";
      }
    }

    if (data && data.meta.filters_applied) {
      data.meta?.filters_applied?.forEach((item) => {
        const values = data.meta?.filters_available[item.facet].type === "and";
        form.setFieldsValue({
          [item.facet]: values ? item.values[0] : item.values,
        });
      });
    }
    if (data && isDesktop && filterDirection !== "horizontal") {
      setActiveKey(
        Object.values(data.meta?.filters_available)?.map(
          (_, i) => `${String(i)}-collapsed`
        )
      );
    } else if (data && !isDesktop) {
      setActiveKey([]);
    }
  }, [data, isDesktop]);

  useEffect(() => {
    form.resetFields();
    if (
      searchDataResults &&
      searchDataResults.meta.filters_applied &&
      Array.isArray(searchDataResults.meta.filters_applied)
    ) {
      searchDataResults.meta?.filters_applied?.forEach((item) => {
        const values =
          searchDataResults.meta?.filters_available[item.facet].type === "and";
        form.setFieldsValue({
          [item.facet]: values ? item.values[0] : item.values,
        });
      });
    }
  }, [searchDataResults]);

  const onChange = (newFilters) => {
    const allFilters = [
      ...selectedFilters,
      ...newFilters.map((field) => ({
        name: field.name[0],
        values: field.value,
      })),
    ];

    let uniqueFilters = [];
    for (let i = 0; i < allFilters.length; i += 1) {
      const { name, values } = allFilters[i];
      const alreadyHaveFilterName = uniqueFilters.find(
        (filter) => filter.name === name
      );

      if (alreadyHaveFilterName) {
        const filterType = Object.entries(data?.meta?.filters_available)?.find(
          ([key]) => key === alreadyHaveFilterName.name
        )[1].type;

        if (filterType === "and") {
          alreadyHaveFilterName.values = values;
        } else {
          const uniqueValues = [...new Set([...values])];
          alreadyHaveFilterName.values = uniqueValues;
        }
      } else {
        uniqueFilters.push({
          name,
          values,
        });
      }
    }

    const finalFilters = uniqueFilters.filter(({ values }) => values.length);

    changeFilter(finalFilters);
    changePage(1);

    if (!isDesktop) {
      resetActive();
    }
  };

  const handleChangeCollapse = (newFields) => {
    setActiveKey(newFields);

    const overlay = document.querySelector(".bnly-search__overlay");
    if (newFields) {
      overlay
        ? (overlay.style.display = "block")
        : (overlay.style.display = "none");
    }
  };

  const handleClickArrow = (index) => {
    setActiveKey(activeKey.filter((i) => Number(i) !== index) + "-collapsed");
  };

  const resetActive = () => {
    setActiveKey([]);
    closeSidebar();
  };

  const forceChangeForm = () => {
    const finalValues = form.getFieldsValue();
    const changeForm = Object.entries(finalValues)
      .filter((item) => (Array.isArray(item[1]) ? item[1].length : item[1]))
      .map((keys) => ({
        name: keys[0],
        values: Array.isArray(keys[1])
          ? keys[1].filter(
              (k) => data.meta?.filters_available[keys[0]].values[k]
            )
          : keys[1] || [],
      }));
    changeFilter(changeForm);
  };

  const closeTag = (e, tagValue, tagName) => {
    e.preventDefault();

    const newFilters = [];
    selectedFilters?.forEach(({ name, values }) => {
      if (tagName === name) {
        const newValues =
          tagName === "price" || typeof values === "string"
            ? ""
            : values.filter((v) => v !== tagValue);

        if (newValues.length) {
          return newFilters.push({ name, values: newValues });
        }

        return "";
      } else {
        return newFilters.push({ name, values });
      }
    });

    changeFilter(newFilters);

    if (!isDesktop) {
      resetActive();
    }
  };

  const clearFilters = (e) => {
    e.preventDefault();
    // changePage(1);
    form.resetFields();
    forceChangeForm();

    if (!isDesktop) {
      resetActive();
    }
  };

  const debounceOnChange = debounceAction(onChange, 200);

  const onFiltersChange = (changedFilter, isSubmitBtn = false) => {
    const changedFilterData = Object.entries(
      data?.meta?.filters_available
    )?.find(([key]) => key === changedFilter[0]?.name[0]);

    const filterType = changedFilterData[1].type;

    if (filterType === "or" || filterType === "range") {
      if (isSubmitBtn) {
        onChange(changedFilter);
      }

      return;
    }

    debounceOnChange(changedFilter);
  };

  if (!data || isLoading) return <FiltersSkeleton />;

  return (
    <CustomCollapseArea
      className="bnly-search__filters-container"
      direction="vertical"
      style={{ width: "100%" }}
      horizontal={layoutType === "fullwidth"}
      filterDirection={filterDirection}
      areFiltersAvailable={areFiltersAvailable}
    >
      <Form
        className="bnly-search__filters-form"
        form={form}
        layout="vertical"
        onFieldsChange={(changedFilter) => onFiltersChange(changedFilter)}
      >
        <FiltersWrapper
          className="bnly-search__filters-wrapper"
          activeKey={activeKey}
          onChange={handleChangeCollapse}
          ghost
          expandIcon={(panelProps) => <CustomIcon {...panelProps} />}
          accordion={filterDirection === "horizontal" && isDesktop}
          filterDirection={filterDirection}
        >
          <CustomHeader>
            <LeftOutlined
              style={{ fontSize: "20px", color: "var(--bn-secondary-color)" }}
              onClick={closeSidebar}
            />
            <h3>Filtros</h3>
          </CustomHeader>

          {isDesktop && (
            <FilterHeader
              showMobile={false}
              selectedFilters={selectedFilters}
              filterData={data}
              closeTag={closeTag}
              clearFilters={clearFilters}
            />
          )}

          {Object.values(data.meta?.filters_available)
            ?.sort((a, b) => a?.order - b?.order)
            ?.filter((filter) => Object.keys(filter.values)?.length > 1)
            ?.map((filter, index) => (
              <CustomPanel
                className="bnly-search__filter-name"
                header={filter.label}
                key={`${index}-collapsed`}
                showLabel={showLabel}
              >
                <ContentPanel>
                  <CustomHeader>
                    <LeftOutlined
                      style={{
                        fontSize: "20px",
                        color: "var(--bn-secondary-color)",
                      }}
                      onClick={() => handleClickArrow(index)}
                    />
                    <h3>{filter.label}</h3>
                  </CustomHeader>
                  {filter.type === "and" && <FilterRadio filter={filter} />}

                  {filter.type === "or" && (
                    <FilterCheckbox
                      filter={filter}
                      onFiltersChange={onFiltersChange}
                    />
                  )}

                  {filter.type === "range" && (
                    <FilterRange
                      filter={filter}
                      onFiltersChange={onFiltersChange}
                    />
                  )}
                </ContentPanel>
              </CustomPanel>
            ))}

          {enableButton && isDesktop && (
            <div style={{ marginTop: 56 }}>
              <Button
                type="primary"
                htmlType="submit"
                size={"large"}
                onClick={resetActive}
              >
                FILTRAR
              </Button>
            </div>
          )}

          {!collapsed && (
            <FooterActions>
              <FilterHeader
                selectedFilters={selectedFilters}
                closeTag={closeTag}
                filterData={data}
                clearFilters={clearFilters}
              />
              <div className="buttons">
                <p>(Total de {data?.meta?.results_count} itens)</p>

                <Button
                  className="bnly-search__filter-close-btn"
                  type="primary"
                  size={"large"}
                  onClick={() => closeSidebar()}
                >
                  Fechar
                </Button>
              </div>
            </FooterActions>
          )}
        </FiltersWrapper>
      </Form>
    </CustomCollapseArea>
  );
};

export default Filters;
